import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLeads } from '../contexts/LeadsContext';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  TablePagination,
} from '@mui/material';
import {
  Add,
  Search,
  Edit,
  Delete,
  Phone,
  Email,
  Visibility,
  ArrowUpward,
  ArrowDownward,
} from '@mui/icons-material';

interface Customer {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  home_phone?: string;
  address_1?: string;
  address_2?: string;
  address_3?: string;
  town?: string;
  postcode: string;
  notes?: string;
  status: 'active' | 'inactive';
  created_at: string;
  source?: string;
  lead_status?: string; // Lead status from leads table, can be null
}

interface AddressOption {
  postcode: string;
  postTown: string;
  address1: string;
  county: string;
}

// Remove mock data - use real data from API

export default function Customers() {
  const { leads, updateLead } = useLeads();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  
  // Pagination state (0-based like Leads page)
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Pagination handlers
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page when changing items per page
  };

  const handleSearchChange = (newSearchTerm: string) => {
    setSearchTerm(newSearchTerm);
    setPage(0); // Reset to first page when searching
  };

  const handleStatusFilterChange = (newStatus: string) => {
    setStatusFilter(newStatus);
    setPage(0); // Reset to first page when filtering
  };
  
  // Fetch customers from API
  useEffect(() => {
    fetchCustomers();
  }, [page, rowsPerPage, searchTerm, statusFilter]);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      
      // Build query parameters (API expects 1-based page)
      const params = new URLSearchParams({
        page: (page + 1).toString(),
        limit: rowsPerPage.toString(),
      });
      
      if (searchTerm.trim()) {
        params.append('search', searchTerm.trim());
      }
      
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      
      const response = await fetch(`/api/customers?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // API returns {customers: [], total: N, page: N, totalPages: N}
        if (data.customers && Array.isArray(data.customers)) {
          // Transform API data to match Customer interface
          const transformedCustomers = data.customers.map((customer: any) => ({
            id: customer.id,
            first_name: customer.first_name,
            last_name: customer.last_name,
            email: customer.email || '',
            phone: customer.phone || '',
            home_phone: customer.home_phone || '',
            address_1: customer.address || '',
            address_2: customer.address_2 || '',
            address_3: customer.address_3 || '',
            town: customer.city || '',
            postcode: customer.postal_code || '',
            notes: customer.notes || '',
            status: customer.status || 'active',
            created_at: customer.created_at,
            source: '',
            lead_status: customer.lead_status || null // Lead status from joined table
          }));
          setCustomers(transformedCustomers);
          
          // Update pagination state
          setTotalCustomers(data.total || 0);
        } else {
          console.error('Invalid API response structure:', data);
          setCustomers([]);
          setTotalCustomers(0);
        }
      } else {
        console.error('API Error:', response.status, response.statusText);
        if (response.status === 401) {
          // Auth error - just log it, don't auto-logout
          console.error('Authentication error 401 - NOT auto-logging out');
        } else {
          const errorText = await response.text();
          console.error('Error details:', errorText);
        }
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState<Customer | null>(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    home_phone: '',
    address_1: '',
    address_2: '',
    address_3: '',
    town: '',
    postcode: '',
    notes: '',
  });

  // Postcode lookup state
  const [postcodeDialogOpen, setPostcodeDialogOpen] = useState(false);
  const [postcodeSearch, setPostcodeSearch] = useState('');
  const [addressOptions, setAddressOptions] = useState<AddressOption[]>([]);
  const [loadingAddresses, setLoadingAddresses] = useState(false);

  // Note: Filtering and sorting now handled server-side via API

  const handleOpenDialog = (customer?: Customer) => {
    if (customer) {
      setEditingCustomer(customer);
      setFormData({
        first_name: customer.first_name,
        last_name: customer.last_name,
        email: customer.email,
        phone: customer.phone,
        home_phone: customer.home_phone || '',
        address_1: customer.address_1 || '',
        address_2: customer.address_2 || '',
        address_3: customer.address_3 || '',
        town: customer.town || '',
        postcode: customer.postcode || '',
        notes: customer.notes || '',  // Include notes when editing
      });
    } else {
      setEditingCustomer(null);
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        home_phone: '',
        address_1: '',
        address_2: '',
        address_3: '',
        town: '',
        postcode: '',
        notes: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCustomer(null);
  };

  const handleDeleteClick = (customer: Customer, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent row click
    setCustomerToDelete(customer);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (customerToDelete) {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`/api/customers/${customerToDelete.id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        
        if (response.ok) {
          // Remove the customer from local state
          setCustomers(prev => prev.filter(c => c.id !== customerToDelete.id));
          setDeleteDialogOpen(false);
          setCustomerToDelete(null);
          // Refresh the list
          await fetchCustomers();
        } else {
          alert('Failed to delete contact. Please try again.');
        }
      } catch (error) {
        console.error('Error deleting customer:', error);
        alert('An error occurred while deleting the contact.');
      }
    }
  };

  // Postcode lookup functions
  const handlePostcodeLookup = async () => {
    if (!postcodeSearch.trim()) {
      alert('Please enter a postcode');
      return;
    }

    setLoadingAddresses(true);
    try {
      // Remove spaces and convert to uppercase
      const cleanPostcode = postcodeSearch.replace(/\s+/g, '').toUpperCase();
      
      // Use the new server-side endpoint
      const response = await axios.get(`/api/postcode/lookup/${cleanPostcode}`);
      
      const data = response.data;
      
      if (data.success && data.addresses && data.addresses.length > 0) {
        setAddressOptions(data.addresses);
      } else if (data.success && data.addresses.length === 0) {
        alert('No addresses found for this postcode');
        setAddressOptions([]);
      } else {
        alert(data.message || 'Error fetching addresses. Please try again.');
        setAddressOptions([]);
      }
    } catch (error: any) {
      console.error('Error fetching addresses:', error);
      const errorMessage = error.response?.data?.message || 'Error fetching addresses. Please try again.';
      alert(errorMessage);
      setAddressOptions([]);
    } finally {
      setLoadingAddresses(false);
    }
  };

  const handleAddressSelect = (address: AddressOption) => {
    setFormData(prev => ({
      ...prev,
      address_1: address.address1,  // e.g. "1 Patterdale Close"
      address_2: '',  // Clear for manual entry
      address_3: address.county,  // e.g. "Lancashire"
      town: address.postTown,  // e.g. "PRESTON"
      postcode: address.postcode  // e.g. "PR1 5TG"
    }));
    
    setPostcodeDialogOpen(false);
    setPostcodeSearch('');
    setAddressOptions([]);
  };

  const handleOpenPostcodeLookup = () => {
    // Pre-populate with the current postcode from the form
    setPostcodeSearch(formData.postcode || '');
    setAddressOptions([]);
    setPostcodeDialogOpen(true);
  };

  const handleSaveCustomer = async () => {
    if (!formData.first_name || !formData.last_name || !formData.phone) {
      return; // Basic validation
    }

    try {
      setLoading(true);

      // Map frontend form data to API expected format
      const customerData = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formData.phone,
        home_phone: formData.home_phone || '',
        address: formData.address_1, // API uses 'address' for address_1
        address_2: formData.address_2 || '',
        address_3: formData.address_3 || '',
        city: formData.town, // API uses 'city' for town
        postal_code: formData.postcode, // API uses 'postal_code' for postcode
        notes: formData.notes || '',
        status: editingCustomer ? editingCustomer.status || 'active' : 'active',
        latitude: null,  // Add these as null or fetch from geocoding
        longitude: null
      };

      if (editingCustomer) {
        // Update existing customer via API
        const response = await fetch(`/api/customers/${editingCustomer.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
          body: JSON.stringify(customerData),
        });

        if (response.ok) {
          // Close dialog first
          setOpenDialog(false);
          setEditingCustomer(null);
          // Then refresh the customer list from API
          await fetchCustomers();
        } else {
          const error = await response.json();
          console.error('Failed to update customer:', error.message);
          alert(`Failed to update customer: ${error.message}`);
          return;
        }
      } else {
        // Create new customer via API
        const response = await fetch('/api/customers', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
          body: JSON.stringify(customerData),
        });

        if (response.ok) {
          const result = await response.json();
          // Refresh the customer list from API
          await fetchCustomers();
        } else {
          const error = await response.json();
          console.error('Failed to create customer:', error.message);
          alert(`Failed to create customer: ${error.message}`);
          return;
        }
      }

      // Only close dialog if we're creating (not updating - update closes earlier)
      if (!editingCustomer) {
        setOpenDialog(false);
      }
    } catch (error) {
      console.error('Error saving customer:', error);
      alert('Error saving customer. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
        >
          Add Contact
        </Button>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' }, 
            gap: 2, 
            alignItems: { md: 'center' } 
          }}>
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 70%' } }}>
              <TextField
                fullWidth
                placeholder="Search contacts..."
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 30%' }, display: 'flex', justifyContent: 'flex-end' }}>
              <Typography variant="body2" color="textSecondary">
                {totalCustomers} contacts found ({customers.length} on this page)
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell onClick={() => handleSort('name')} sx={{ cursor: 'pointer' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Name
                  {sortField === 'name' && (
                    sortDirection === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />
                  )}
                </Box>
              </TableCell>
              <TableCell onClick={() => handleSort('email')} sx={{ cursor: 'pointer' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Contact
                  {sortField === 'email' && (
                    sortDirection === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />
                  )}
                </Box>
              </TableCell>
              <TableCell onClick={() => handleSort('address')} sx={{ cursor: 'pointer' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Address
                  {sortField === 'address' && (
                    sortDirection === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />
                  )}
                </Box>
              </TableCell>
              <TableCell onClick={() => handleSort('created_at')} sx={{ cursor: 'pointer' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Created
                  {sortField === 'created_at' && (
                    sortDirection === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />
                  )}
                </Box>
              </TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {customers.map((customer) => (
              <TableRow 
                key={customer.id}
                onClick={() => handleOpenDialog(customer)}
                sx={{ 
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                    backgroundColor: 'action.hover',
                  }
                }}
              >
                <TableCell>
                  <Typography variant="body2" fontWeight="bold">
                    {customer.first_name} {customer.last_name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{customer.email}</Typography>
                  <Typography variant="caption" color="textSecondary">
                    {customer.phone}
                    {customer.home_phone && ` • ${customer.home_phone}`}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {customer.address_1 && `${customer.address_1}`}
                    {customer.address_2 && `, ${customer.address_2}`}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {customer.town && `${customer.town}, `}{customer.postcode}
                  </Typography>
                </TableCell>
                <TableCell>
                  {new Date(customer.created_at).toLocaleDateString()}
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent row click event
                      if (customer.phone) {
                        window.location.href = `tel:${customer.phone}`;
                      }
                    }}
                    title={`Call ${customer.phone || 'No phone number'}`}
                    disabled={!customer.phone}
                    sx={{ 
                      color: customer.phone ? 'success.main' : 'disabled',
                      '&:hover': {
                        backgroundColor: customer.phone ? 'success.light' : 'transparent'
                      }
                    }}
                  >
                    <Phone />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent row click event
                      if (customer.email) {
                        window.location.href = `mailto:${customer.email}`;
                      }
                    }}
                    title={`Email ${customer.email || 'No email address'}`}
                    disabled={!customer.email}
                    sx={{ 
                      color: customer.email ? 'primary.main' : 'disabled',
                      '&:hover': {
                        backgroundColor: customer.email ? 'primary.light' : 'transparent'
                      }
                    }}
                  >
                    <Email />
                  </IconButton>
                  <IconButton 
                    size="small" 
                    title="Delete contact"
                    onClick={(e) => handleDeleteClick(customer, e)}
                    sx={{ color: 'error.main' }}
                  >
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={totalCustomers}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      {/* Add/Edit Contact Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingCustomer ? 'Edit Contact' : 'Add Contact'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
              <TextField
                fullWidth
                label="First Name *"
                value={formData.first_name}
                onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
              />
              <TextField
                fullWidth
                label="Last Name *"
                value={formData.last_name}
                onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
              />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              />
              <TextField
                fullWidth
                label="Phone *"
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
              />
              <TextField
                fullWidth
                label="Home Phone"
                value={formData.home_phone}
                onChange={(e) => setFormData(prev => ({ ...prev, home_phone: e.target.value }))}
              />
            </Box>
            <Box sx={{ 
              display: 'flex', 
              gap: 3, 
              flexDirection: { xs: 'column', lg: 'row' } 
            }}>
              {/* Address Section */}
              <Box sx={{ 
                flex: '1 1 50%', 
                border: '2px solid #e0e0e0', 
                borderRadius: 2, 
                padding: 2 
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                    Address
                  </Typography>
                  <Tooltip title="Search via Postcode" arrow>
                    <IconButton
                      onClick={handleOpenPostcodeLookup}
                      size="small"
                      sx={{ color: 'primary.main' }}
                    >
                      <Search fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    fullWidth
                    label="Address Line 1"
                    value={formData.address_1}
                    onChange={(e) => setFormData(prev => ({ ...prev, address_1: e.target.value }))}
                    size="small"
                  />
                  <TextField
                    fullWidth
                    label="Address Line 2"
                    value={formData.address_2}
                    onChange={(e) => setFormData(prev => ({ ...prev, address_2: e.target.value }))}
                    size="small"
                  />
                  <TextField
                    fullWidth
                    label="Address Line 3"
                    value={formData.address_3}
                    onChange={(e) => setFormData(prev => ({ ...prev, address_3: e.target.value }))}
                    size="small"
                  />
                  <TextField
                    fullWidth
                    label="Town"
                    value={formData.town}
                    onChange={(e) => setFormData(prev => ({ ...prev, town: e.target.value }))}
                    size="small"
                  />
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      fullWidth
                      label="Postcode"
                      value={formData.postcode}
                      onChange={(e) => setFormData(prev => ({ ...prev, postcode: e.target.value }))}
                      size="small"
                    />
                    <Tooltip title="Lookup address by postcode">
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleOpenPostcodeLookup}
                        sx={{ whiteSpace: 'nowrap' }}
                      >
                        Lookup
                      </Button>
                    </Tooltip>
                  </Box>
                </Box>
              </Box>
            </Box>
            <TextField
              fullWidth
              label="Notes"
              multiline
              rows={3}
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleSaveCustomer} 
            variant="contained"
            disabled={loading || !formData.first_name || !formData.last_name || !formData.phone}
          >
            {loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              editingCustomer ? 'Update Contact' : 'Create Contact'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Postcode Lookup Dialog */}
      <Dialog open={postcodeDialogOpen} onClose={() => setPostcodeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Postcode Lookup</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Enter Postcode"
              value={postcodeSearch}
              onChange={(e) => setPostcodeSearch(e.target.value)}
              placeholder="e.g. BL1 1AA"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handlePostcodeLookup();
                }
              }}
            />
            <Button
              variant="contained"
              onClick={handlePostcodeLookup}
              disabled={loadingAddresses || !postcodeSearch.trim()}
            >
              {loadingAddresses ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                'Search Addresses'
              )}
            </Button>
            
            {addressOptions.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Select an address:
                </Typography>
                <List sx={{ maxHeight: 300, overflowY: 'auto' }}>
                  {addressOptions.map((address, index) => (
                    <ListItem
                      key={index}
                      component="div"
                      onClick={() => handleAddressSelect(address)}
                      sx={{
                        cursor: 'pointer',
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1,
                        mb: 1,
                        '&:hover': {
                          bgcolor: 'action.hover',
                        },
                      }}
                    >
                      <ListItemText
                        primary={address.address1}
                        secondary={`${address.postTown}, ${address.county} ${address.postcode}`}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPostcodeDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography>
            Do you really want to delete this contact?
          </Typography>
          {customerToDelete && (
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              {customerToDelete.first_name} {customerToDelete.last_name}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setDeleteDialogOpen(false);
              setCustomerToDelete(null);
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="error"
            onClick={handleDeleteConfirm}
          >
            Delete Contact
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}